// プロジェクトアーカイブ（.zip）の純粋ロジック。
// 用途: ブラウザ内の作業データ一式をローカルPCへ保存し、別環境（localhost ⇄ create.voxelnovels.com）へ
//       持ち込む／バックアップする／似た別ゲームの土台として複製する。
// 入出力（IndexedDB・ZIP 組み立て）は backend 側が担当し、ここでは中身の組み立てと検証だけを行う。
//
// レイアウト:
//   manifest.json      … 形式・元プロジェクト情報・ファイル一覧（1件ずつ area/path/size/encoding）
//   project/<path>     … projectFiles（projects/<projectId>/ 相対。物語・解析・プロンプト・生成画像）
//   preview/<path>     … previewFiles（newgames/<gameId>/ 相対。ゲーム本体 maps/**・cast/**）
//   assets/<path>      … assetFiles（assets/ 相対 = newgames/<gameId>/models/**。Meshy 生成 GLB）

export const ARCHIVE_KIND = 'voxelnovels-project-archive';
export const ARCHIVE_FORMAT_VERSION = '1.0';
export const MANIFEST_NAME = 'manifest.json';
export const AREAS = ['project', 'preview', 'assets'];
export const AREA_PREFIX = { project: 'project/', preview: 'preview/', assets: 'assets/' };
export const GAME_ID_PATTERN = /^[a-z][a-z0-9_-]{2,39}$/;

export class ArchiveError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ArchiveError';
    }
}

const encoder = new TextEncoder();
const byteLength = (data) => (typeof data === 'string' ? encoder.encode(data).length : (data?.byteLength ?? 0));
const encodingOf = (data) => (typeof data === 'string' ? 'utf8' : 'binary');

/** アーカイブ内のファイル名（area とパスから決まる。パス走査は入口で弾く）。 */
export function entryName(area, path) {
    if (!AREA_PREFIX[area]) throw new ArchiveError(`不正な領域です: ${area}`);
    const clean = String(path ?? '').replace(/^\/+/, '');
    if (!clean || clean.includes('..') || clean.includes('//') || clean.endsWith('/')) {
        throw new ArchiveError(`不正なファイルパスです: ${path}`);
    }
    return `${AREA_PREFIX[area]}${clean}`;
}

/** アーカイブ内のファイル名を area とパスへ戻す。 */
export function parseEntryName(name) {
    for (const area of AREAS) {
        const prefix = AREA_PREFIX[area];
        if (name.startsWith(prefix)) return { area, path: name.slice(prefix.length) };
    }
    return null;
}

/**
 * エクスポートする manifest と ZIP エントリを組み立てる。
 * @param {object} params
 * @param {object} params.meta projects ストアのメタ行
 * @param {Array<{path: string, data: string|Uint8Array}>} params.projectFiles
 * @param {Array<{path: string, data: string|Uint8Array}>} params.previewFiles
 * @param {Array<{path: string, data: string|Uint8Array}>} params.assetFiles
 */
export function buildArchive({ meta, projectFiles = [], previewFiles = [], assetFiles = [], now = new Date(), appVersion = null }) {
    const projectId = meta?.projectId;
    if (!projectId) throw new ArchiveError('プロジェクト情報が読み取れませんでした。');
    const sources = { project: projectFiles, preview: previewFiles, assets: assetFiles };
    const files = [];
    const entries = [];
    const counts = {};
    const bytes = {};
    for (const area of AREAS) {
        counts[area] = 0;
        bytes[area] = 0;
        for (const file of sources[area]) {
            const name = entryName(area, file.path);
            files.push({ area, path: file.path, size: byteLength(file.data), encoding: encodingOf(file.data) });
            entries.push({ name, data: file.data });
            counts[area] += 1;
            bytes[area] += byteLength(file.data);
        }
    }
    const manifest = {
        kind: ARCHIVE_KIND,
        formatVersion: ARCHIVE_FORMAT_VERSION,
        exportedAt: now.toISOString(),
        appVersion,
        project: {
            projectId,
            gameId: meta.gameId ?? null,
            title: meta.title ?? null,
            novel: meta.novel ?? null,
            meta,
        },
        counts: { ...counts, total: files.length },
        bytes: { ...bytes, total: AREAS.reduce((a, k) => a + bytes[k], 0) },
        files,
    };
    entries.unshift({ name: MANIFEST_NAME, data: JSON.stringify(manifest, null, 2) });
    return { manifest, entries };
}

/** 保存ファイル名（gameId かプロジェクトIDと日時から作る。OS で扱える文字だけにする）。 */
export function archiveFileName(manifest, now = new Date()) {
    const base = String(manifest?.project?.gameId || manifest?.project?.projectId || 'project')
        .replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 40);
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    return `voxelnovels-${base}-${stamp}.zip`;
}

/** manifest を検証して返す（利用者にそのまま出せる日本語メッセージで失敗する）。 */
export function parseManifest(text) {
    let manifest;
    try {
        manifest = typeof text === 'string' ? JSON.parse(text) : text;
    } catch {
        throw new ArchiveError('ファイルの中身を読み取れませんでした（manifest.json が壊れています）。');
    }
    if (!manifest || manifest.kind !== ARCHIVE_KIND) {
        throw new ArchiveError('VoxelNovels のプロジェクトファイルではありません。エクスポートで保存した .zip を選んでください。');
    }
    const major = String(manifest.formatVersion ?? '').split('.')[0];
    if (major !== ARCHIVE_FORMAT_VERSION.split('.')[0]) {
        throw new ArchiveError(`このファイルの形式（${manifest.formatVersion}）には未対応です。ツールを最新にしてから取り込んでください。`);
    }
    if (!manifest.project?.projectId || !Array.isArray(manifest.files)) {
        throw new ArchiveError('ファイルの中身が不完全です（プロジェクト情報がありません）。');
    }
    for (const file of manifest.files) {
        if (!AREAS.includes(file?.area)) throw new ArchiveError(`未知の領域が含まれています: ${file?.area}`);
        entryName(file.area, file.path); // 走査・空パスをここで弾く
    }
    return manifest;
}

/** 画面表示用の要約。 */
export function summarizeArchive(manifest) {
    return {
        projectId: manifest.project.projectId,
        gameId: manifest.project.gameId ?? null,
        title: manifest.project.title ?? null,
        exportedAt: manifest.exportedAt ?? null,
        counts: manifest.counts ?? {},
        bytes: manifest.bytes ?? {},
        hasModels: (manifest.counts?.assets ?? 0) > 0,
    };
}

/** 既存と衝突しない projectId（採番規則は Node 版 story-project.mjs と同じ）。 */
export function newProjectId(now = new Date(), existingProjectIds = []) {
    const pad = (n) => String(n).padStart(2, '0');
    const base = `story-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`
        + `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const taken = new Set(existingProjectIds);
    if (!taken.has(base)) return base;
    for (let i = 2; ; i += 1) {
        const candidate = `${base}-${i}`;
        if (!taken.has(candidate)) return candidate;
    }
}

/** 複製用の gameId 候補（`<元>_copy` → 衝突したら連番）。 */
export function suggestCloneGameId(oldGameId, existingGameIds = []) {
    const taken = new Set(existingGameIds.filter(Boolean));
    const base = String(oldGameId || 'game').replace(/[^a-z0-9_-]/g, '').slice(0, 33) || 'game';
    const first = `${base}_copy`;
    if (GAME_ID_PATTERN.test(first) && !taken.has(first)) return first;
    for (let i = 2; i < 100; i += 1) {
        const candidate = `${base}_copy${i}`;
        if (GAME_ID_PATTERN.test(candidate) && !taken.has(candidate)) return candidate;
    }
    return `${base}_copy`;
}

/** 複製時の gameId 検証（形式・予約語・既存重複）。 */
export function validateNewGameId(gameId, { reserved = [], existingGameIds = [] } = {}) {
    if (!GAME_ID_PATTERN.test(String(gameId ?? ''))) {
        throw new ArchiveError('ゲームIDは英小文字で始まる 3〜40文字（英数字・_・-）で入力してください。');
    }
    if (reserved.includes(gameId)) throw new ArchiveError(`ゲームID「${gameId}」は予約語のため使用できません。`);
    if (existingGameIds.includes(gameId)) throw new ArchiveError(`ゲームID「${gameId}」は既に使われています。別のIDにしてください。`);
    return gameId;
}

/**
 * 取り込み方法を確定する。
 * @param {object} manifest
 * @param {object} options
 * @param {'restore'|'clone'} options.mode
 * @param {string} [options.newGameId] clone のときの新しい gameId
 * @param {string} [options.newTitle] clone のときの新しいタイトル（任意）
 * @param {{projectIds: string[], gameIds: string[], gameIdOwners: Record<string,string>}} options.existing
 * @param {string[]} [options.reserved]
 * @returns {{mode, projectId, gameId, overwrite, rename: {oldGameId, newGameId}|null, title}}
 */
export function planImport(manifest, { mode, newGameId, newTitle, existing, reserved = [], now = new Date() }) {
    const oldProjectId = manifest.project.projectId;
    const oldGameId = manifest.project.gameId ?? null;
    const projectIds = existing?.projectIds ?? [];
    const gameIdOwners = existing?.gameIdOwners ?? {};

    if (mode === 'restore') {
        // 同じ gameId を別プロジェクトが使っていると、ゲーム本体・生成モデルを奪ってしまう
        const owner = oldGameId ? gameIdOwners[oldGameId] : undefined;
        if (owner && owner !== oldProjectId) {
            throw new ArchiveError(
                `ゲームID「${oldGameId}」は別のプロジェクトが使用中です。「別ゲームとして複製」で取り込んでください。`,
            );
        }
        return {
            mode, projectId: oldProjectId, gameId: oldGameId,
            overwrite: projectIds.includes(oldProjectId), rename: null,
            title: manifest.project.title ?? null,
        };
    }
    if (mode !== 'clone') throw new ArchiveError('取り込み方法が不正です。');

    const gameId = oldGameId
        ? validateNewGameId(newGameId || suggestCloneGameId(oldGameId, Object.keys(gameIdOwners)), {
            reserved, existingGameIds: Object.keys(gameIdOwners),
        })
        : null;
    const title = newTitle?.trim()
        ? { ...(manifest.project.title ?? {}), ja: newTitle.trim() }
        : (manifest.project.title ?? null);
    return {
        mode, projectId: newProjectId(now, projectIds), gameId, overwrite: false,
        rename: oldGameId && gameId && oldGameId !== gameId ? { oldGameId, newGameId: gameId } : null,
        title,
    };
}

/**
 * 複製時のパス・参照の書き換え。
 * - assets のキーは `newgames/<gameId>/…` を含むため付け替える
 * - 生成 GLB の参照は JSON 内に `../assets/newgames/<gameId>/models/…` として埋まっているため置換する
 */
export function rewriteEntryForPlan({ area, path, data }, plan) {
    let nextPath = path;
    let nextData = data;
    const rename = plan.rename;
    if (rename) {
        const from = `newgames/${rename.oldGameId}/`;
        const to = `newgames/${rename.newGameId}/`;
        if (area === 'assets' && nextPath.startsWith(from)) nextPath = to + nextPath.slice(from.length);
        if (typeof nextData === 'string' && nextData.includes(from)) nextData = nextData.split(from).join(to);
    }
    if (area === 'project' && path === 'project.json' && typeof nextData === 'string') {
        nextData = rewriteProjectJson(nextData, plan);
    }
    if (area === 'project' && path === 'analysis.json' && typeof nextData === 'string' && plan.mode === 'clone') {
        nextData = rewriteAnalysisTitle(nextData, plan.title);
    }
    return { area, path: nextPath, data: nextData };
}

function rewriteProjectJson(text, plan) {
    try {
        const json = JSON.parse(text);
        json.projectId = plan.projectId;
        if (plan.gameId !== undefined) json.gameId = plan.gameId;
        return JSON.stringify(json, null, 2);
    } catch {
        return text; // 壊れた project.json はそのまま入れて、以降の画面で検知させる
    }
}

function rewriteAnalysisTitle(text, title) {
    if (!title?.ja) return text;
    try {
        const json = JSON.parse(text);
        if (json?.game?.title && typeof json.game.title === 'object') {
            json.game.title = { ...json.game.title, ja: title.ja };
            return JSON.stringify(json, null, 2);
        }
        return text;
    } catch {
        return text;
    }
}

/** 取り込み後の projects メタ行を作る。 */
export function buildImportedMeta(manifest, plan, { now = new Date() } = {}) {
    const base = manifest.project.meta && typeof manifest.project.meta === 'object' ? { ...manifest.project.meta } : {};
    return {
        ...base,
        projectId: plan.projectId,
        gameId: plan.gameId ?? null,
        title: plan.title ?? base.title ?? null,
        importedAt: now.toISOString(),
        importedFrom: { projectId: manifest.project.projectId, gameId: manifest.project.gameId ?? null, exportedAt: manifest.exportedAt ?? null },
    };
}
